import {observer} from 'mobx-react-lite'
import React from 'react'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {UserSettings} from '@client/user-settings'
import {Tracking, TrackingEvent} from '@common/tracking'
import {UtilUi} from '@common/utils-ui'
import {TextLoader} from '@components/common/base/text-loader'
import {TextWithUrl} from '@components/common/base/text-with-url'
import {DivWithHint} from '@components/common/hint/hint'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {MessageBox} from '@components/message-box'
import {MyTasksListProps} from '@components/my-tasks/my-tasks-list'
import {TaskItem} from '@components/my-tasks/task-item'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {TaskMenu} from '@components/task-menu/task-menu'
import {
  keyboardHandler,
  noPropagate,
  textCursorSelectAll,
  useInput,
  useLoadedLazyTaskEffect
} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {Sticker} from '@extensions/sticker/sticker'
import {UserStickerState} from '@extensions/sticker/views-web/user/user-sticker-state'
import {canAssignUser} from '@extensions/sticker/views-web/user-view'
import {TaskPage} from './my-tasks-conf'

export interface DragControl<Id> {
  dragStart(id: Id): (e: React.DragEvent) => void
  dragEnd(e: React.DragEvent<HTMLElement>): void
  dragEnd(e: React.DragEvent<HTMLElement>): void
  drop(to: Id): (e: React.DragEvent) => void
  dragEnter(inside: Id): (e: React.DragEvent) => void
  dragLeave(inside: Id): (e: React.DragEvent) => void
  dragOver(inside: Id): (e: React.DragEvent) => void
  dropToBeginning(id: Id): void
}

interface Props {
  task: TaskItem
  disabled: boolean
  listProps: MyTasksListProps
  dragCtl: DragControl<DataObjectId>
  draggable: boolean
  withAssigned: boolean
  page: TaskPage
}

// eslint-disable-next-line max-lines-per-function
export const MyTasksListItem = observer((props: Props) => {
  const {task, listProps, disabled, dragCtl, draggable, withAssigned} = props

  const {draggedId, itemClick, toggleCompleted} = listProps
  const [renaming, setRenaming] = React.useState(false)
  const [newName, onChange, setNewName] = useInput('')
  const startRename = () => {
    setNewName(task.title)
    setRenaming(true)
  }
  const height = renaming
    ? UtilUi.calcTextBounds(newName, {
        lineHeight: '18px',
        fontSize: '14px',
        width: '288px'
      }).height + 4
    : 0
  const stopRename = () => {
    setRenaming(false)
    return true
  }
  const saveName = () => {
    if (newName.trim() && newName.trim() !== task.title) {
      const box = Sync.getCurrentSandbox()
      box.editTitle(box.get(task.id) as Hub, newName.trim())
    }
    stopRename()
  }
  const keyDown = keyboardHandler({enter: saveName, escape: stopRename})

  const bem = Util.bemFrom('my-tasks')
  const stage = task.stage
  const box = Sync.get(task)
  const hub = box.get(task.id) as Hub
  useLoadedLazyTaskEffect(hub, 'my-tasks-list-item')

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!PermissionsChecker.checkComplete(hub)) {
      MessageBox.warn(Lang.msg.permissionsFobiddenTaskComplete)
      return
    }
    if (
      PermissionsChecker.shouldFallbackToOldCheckComplete(hub) &&
      !PermissionsChecker.oldCheckComplete(hub)
    ) {
      MessageBox.warn(Lang.msg.permissionsFobiddenTaskComplete)
      return
    }
    toggleCompleted(task)
  }
  const done = stage === 'done'
  const hintText = done
    ? Lang.msg.markTaskUncompleted(Time.formatDateTime(task.completedAt))
    : Lang.msg.markTaskCompleted

  const toggleStar = () => {
    dragCtl.dropToBeginning(task.id)
    const value = !task.starred
    if (value) {
      Tracking.send(TrackingEvent.myTasksFavouriteTaskChatStarredOn)
    } else {
      Tracking.send(TrackingEvent.myTasksFavouriteTaskChatStarredOff)
    }
    UserSettings.setStarred(
      task,
      !task.starred,
      props.page === 'starred' ? 'my-tasks-favourite' : 'my-tasks'
    )
  }

  const onAssign = (users: DataObjectId[]) => {
    Sticker.setUsers(hub, users, 'stats')
  }

  const disableDrag = (e: React.DragEvent) => {
    e.dataTransfer.dropEffect = 'none'
  }

  const openedTaskId =
    rightPanelStore.state.chatPanel.opened &&
    rightPanelStore.state.currentChat?.id

  const softLimit = 150
  const hardLimit = 1024
  const realTitle = task.title || Lang.msg.unnamed
  const tooltipTitle = Util.limitStr(realTitle, hardLimit)
  const hideTooltip = realTitle.length <= softLimit

  const allUsers = box.getAllUsers(false).map(u => u.id)
  const calculatedHierarchy = DepartmentsClient.getHierarchy(box)
  const allAvailableUsers =
    props.page === 'by-me'
      ? allUsers
      : allUsers.filter(uid => canAssignUser(hub, uid, calculatedHierarchy))

  return (
    <div
      key={task.id}
      className={bem('task', {
        open: openedTaskId === task.id,
        unseen: !task.stage && task.assigned.includes(globals.currentUserId),
        dragged: draggedId === task.id,
        renaming,
        completed: !!task.completedAt,
        withAssigned
      })}
      draggable={draggable}
      onDragStart={draggable ? dragCtl.dragStart(task.id) : undefined}
      onDragEnd={draggable ? dragCtl.dragEnd : undefined}
      onDrop={draggable ? dragCtl.drop(task.id) : undefined}
      onDragEnter={draggable ? dragCtl.dragEnter(task.id) : disableDrag}
      onDragLeave={draggable ? dragCtl.dragLeave(task.id) : undefined}
      onDragOver={draggable ? dragCtl.dragOver(task.id) : undefined}
      onClick={!renaming ? itemClick(task) : Util.noop}
    >
      <div
        className={bem('toggle', {
          disabled: disabled
        })}
      >
        {draggable && <div className={bem('drag-icon')} />}
        <DivWithHint
          content={hintText}
          className={bem('toggle-icon', {done, undraggable: !draggable})}
          onClick={toggle}
        />
      </div>
      <div className={bem('task-title')}>
        {renaming ? (
          <textarea
            className={bem('task-rename')}
            autoFocus={true}
            value={newName}
            onChange={onChange}
            onKeyDown={keyDown}
            onFocus={textCursorSelectAll}
            onBlur={saveName}
            style={{height}}
          />
        ) : (
          <Tooltip
            offset={[0, 10]}
            content={tooltipTitle}
            disabled={hideTooltip}
          >
            <span>
              {hub.isLazy && <TextLoader />}
              {!hub.isLazy && (
                <TextWithUrl
                  className="pointer-events-auto"
                  text={realTitle}
                  bottom={-3}
                  limit={softLimit}
                />
              )}
            </span>
          </Tooltip>
        )}
      </div>
      <div
        className={bem('stickers flex center justify-end', {
          disabled: disabled
        })}
        onClick={noPropagate}
      >
        {task.renderDeadline()}
      </div>
      {withAssigned && (
        <div
          className={bem('assigned', {
            disabled: disabled
          })}
          onClick={noPropagate}
        >
          <UserStickerState
            users={task.assigned}
            options={allAvailableUsers}
            onChoose={onAssign}
            maxShow={3}
          />
        </div>
      )}
      <div
        className={bem('actions', {
          disabled: disabled
        })}
        onClick={noPropagate}
      >
        <div className={bem('delim')} />
        <DivWithHint
          content={Lang.context.common.starHint}
          className={bem('star', {active: task.starred})}
          onClick={toggleStar}
        />
        <div className={bem('delim')} />
        <div className={bem('menu')}>
          <TaskMenu task={task} location="my-tasks" rename={startRename} />
        </div>
      </div>
    </div>
  )
})
